import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import { brl, lerNumero, markupDe, precoParaCmv, textoMarkup } from "./lib/precos";

/** Um dos 3 preços sugeridos: a meta de CMV (editável, vale para a loja toda),
 *  o preço que dá essa meta, o markup e o lucro. "Usar este preço" só troca o
 *  campo de preço do produto; grava no Salvar. */
export default function MetaCmv({ indice, meta, custo, precoAtual, sufixo, onTrocarMeta, onUsar }) {
  const [txt, setTxt] = useState(String(meta).replace(".", ","));
  useEffect(() => { setTxt(String(meta).replace(".", ",")); }, [meta]);

  const preco = precoParaCmv(custo, meta);
  const ativo = preco > 0 && Math.abs((Number(precoAtual) || 0) - preco) < 0.005;

  const confirmar = async () => {
    const n = lerNumero(txt);
    if (!(n >= 1 && n <= 99)) {
      toast.error("O CMV precisa ficar entre 1% e 99%.");
      setTxt(String(meta).replace(".", ","));
      return;
    }
    const v = Math.round(n * 10) / 10;
    if (v === meta) { setTxt(String(meta).replace(".", ",")); return; }
    const ok = await onTrocarMeta(indice, v);
    if (!ok) setTxt(String(meta).replace(".", ","));
  };

  return (
    <div
      data-e2e={`precificacao-meta-${indice}`}
      className={`rounded-xl border p-3 flex flex-col gap-2 transition-colors ${
        ativo ? "border-emerald-500 bg-emerald-50/70 dark:bg-emerald-950/20" : "bg-muted/20"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Meta de CMV</span>
        <label className="flex items-center gap-0.5 text-sm font-bold text-foreground">
          <input
            data-e2e={`precificacao-meta-input-${indice}`}
            type="text"
            inputMode="decimal"
            value={txt}
            onChange={(e) => setTxt(e.target.value.replace(/[^\d.,]/g, ""))}
            onBlur={confirmar}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
              if (e.key === "Escape") { e.preventDefault(); setTxt(String(meta).replace(".", ",")); }
            }}
            aria-label={`Meta de CMV ${indice + 1} em %`}
            className="w-12 h-7 rounded-md border bg-background px-1.5 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          %
        </label>
      </div>

      <p className="text-2xl font-black tabular-nums tracking-tight text-foreground">
        {preco > 0 ? brl(preco) : "—"}
        {preco > 0 && sufixo && <span className="text-xs font-bold text-muted-foreground ml-0.5">{sufixo}</span>}
      </p>
      <p className="text-[11px] text-muted-foreground leading-tight">
        Markup <strong className="text-foreground">{preco > 0 ? textoMarkup(markupDe(preco, custo)) : "—"}</strong>
        {" · "}Lucro <strong className="text-foreground">{preco > 0 ? brl(preco - custo) : "—"}</strong>
      </p>

      <Button
        type="button"
        size="sm"
        variant={ativo ? "secondary" : "outline"}
        disabled={ativo || preco <= 0}
        onClick={() => onUsar(preco)}
        data-e2e={`precificacao-usar-${indice}`}
        className="mt-auto h-8 text-xs font-bold"
      >
        {ativo ? "✓ Preço atual" : "Usar este preço"}
      </Button>
    </div>
  );
}
